import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from './supabase';
import { formatTokenCount } from './aiTokens';

/**
 * After Stripe Checkout for AI tokens, confirm + credit on return.
 * Looks for ?ai_tokens_checkout=1&session_id=cs_...
 */
export function useAiTokenCheckoutReturn(onCredited?: () => void) {
  const location = useLocation();
  const navigate = useNavigate();
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const flag = params.get('ai_tokens_checkout');
    const sessionId = params.get('session_id');

    if (flag === 'cancelled') {
      params.delete('ai_tokens_checkout');
      params.delete('session_id');
      const next = params.toString();
      navigate(`${location.pathname}${next ? `?${next}` : ''}`, { replace: true });
      toast('Token purchase cancelled');
      return;
    }

    if (flag !== '1' || !sessionId) return;
    if (handledRef.current === sessionId) return;
    handledRef.current = sessionId;

    void (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('purchase-ai-tokens', {
          body: { action: 'confirm-checkout', sessionId },
        });
        if (error || !data?.success) {
          console.error('AI token checkout confirm failed:', error || data);
          toast.error(data?.error || 'Could not confirm token purchase');
        } else {
          const added = Number(data.tokensAdded || 0);
          toast.success(
            data.alreadyProcessed
              ? 'Token purchase already applied'
              : `Added ${formatTokenCount(added)} AI tokens`,
          );
          onCredited?.();
        }
      } catch (err) {
        console.error(err);
        toast.error('Could not confirm token purchase');
      } finally {
        params.delete('ai_tokens_checkout');
        params.delete('session_id');
        const next = params.toString();
        navigate(`${location.pathname}${next ? `?${next}` : ''}`, { replace: true });
      }
    })();
  }, [location.pathname, location.search, navigate, onCredited]);
}
